import React, { useState, memo, useMemo, useCallback } from "react";
import "./LoyaltyOfferCard.css";

const DESCRIPTION_TRUNCATE_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 300;
const PLACEHOLDER_IMAGE = "/loyaltyImages/placeholder.png";

function formatPhoneNumber(number) {
  const cleaned = number.replace(/[^\d+]/g, "");

  // UK example: +44 20 8392 2286
  if (cleaned.startsWith("+44") && cleaned.length === 13) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 5)} ${cleaned.slice(
      5,
      9
    )} ${cleaned.slice(9)}`;
  }

  // Generic fallback formatter (splits nicely)
  return cleaned.replace(
    /(\+?\d{1,3})(\d{3})(\d{3})(\d{3,4})?/,
    (_, p1, p2, p3, p4) => [p1, p2, p3, p4].filter(Boolean).join(" ")
  );
}

const LoyaltyOfferCard = memo(({ offer }) => {
  const [showFullDescription, setShowFullDescription] = useState(false);

  const descriptionData = useMemo(() => {
    const hasDescription = offer?.description?.length > 0;
    const needsTruncation =
      hasDescription && offer.description.length > DESCRIPTION_MAX_LENGTH;

    const displayedDescription = hasDescription
      ? showFullDescription || !needsTruncation
        ? offer.description
        : `${offer.description.slice(0, DESCRIPTION_TRUNCATE_LENGTH)}...`
      : "";

    return { hasDescription, needsTruncation, displayedDescription };
  }, [offer?.description, showFullDescription]);

  const validOffers = useMemo(() => {
    if (!offer) return [];
    return [
      offer.offerOne,
      offer.offerTwo,
      offer.offerThree,
      offer.offerFour,
      offer.offerFive,
    ].filter(Boolean);
  }, [offer]);

  const handleRedirect = useCallback(() => {
    if (offer?.redirectUrl) {
      try {
        window.open(offer.redirectUrl, "_blank", "noopener,noreferrer");
      } catch (error) {
        console.error("Failed to open redirect URL:", error);
      }
    }
  }, [offer?.redirectUrl]);

  const handleImageError = useCallback((e) => {
    if (e.target.src !== PLACEHOLDER_IMAGE) {
      e.target.onerror = null;
      e.target.src = PLACEHOLDER_IMAGE;
    }
  }, []);

  const toggleDescription = useCallback((e) => {
    e.stopPropagation();
    setShowFullDescription((prev) => !prev);
  }, []);

  const handleButtonClick = useCallback(
    (e) => {
      e.stopPropagation();
      handleRedirect();
    },
    [handleRedirect]
  );

  if (!offer?.name) {
    return null;
  }

  const cardStyle = offer.specialBackground
    ? {
        backgroundColor: offer.specialBackground,
        background: offer.specialBackground,
      }
    : {};

  return (
    <div
      className={`loyalty-card-horizontal ${offer.specialBackground ? "loyalty-card-special" : ""} ${offer.isPremiumPartner ? "loyalty-card-premium" : ""}`}
      style={cardStyle}
    >
      {offer.isPremiumPartner && (
        <div className="premium-partner-badge">
          <span className="premium-badge-text">Premium Partner</span>
        </div>
      )}
      <div className="loyalty-card-heading">
        <h3 className="loyalty-card-title">{offer.name}</h3>
        {offer.subtitle && (
          <div className="loyalty-card-subtitle">{offer.subtitle}</div>
        )}
      </div>

      <img
        src={`/loyaltyImages/${offer.imageFileName}`}
        alt={`${offer.name} logo`}
        loading="lazy"
        className="loyalty-card-image"
        onError={handleImageError}
      />

      <div className="loyalty-card-info">
        {descriptionData.hasDescription && (
          <div className="loyalty-card-description-container">
            <p className="loyalty-card-description">
              {descriptionData.displayedDescription}
            </p>

            {descriptionData.needsTruncation && (
              <button
                className="loyalty-card-read-more"
                onClick={toggleDescription}
                type="button"
                aria-expanded={showFullDescription}
                aria-label={
                  showFullDescription
                    ? "Свернуть описание"
                    : "Развернуть описание"
                }
              >
                {showFullDescription
                  ? "Свернуть описание"
                  : "Читать полное описание"}
              </button>
            )}
          </div>
        )}

        {validOffers.length > 0 && (
          <div className="loyalty-card-offers">
            {validOffers.map((offerText, index) => (
              <div
                key={index}
                className="offer-item"
              >
                {offerText}
              </div>
            ))}
          </div>
        )}

        {/* Move the actions inside the info area */}
        <div className="loyalty-card-actions">
          <button
            className="loyalty-card-btn"
            onClick={handleButtonClick}
            type="button"
            disabled={!offer.redirectUrl}
            aria-label={`Забронировать услугу ${offer.name}`}
          >
            Заказать услугу по спецпредложению
          </button>
        </div>
      </div>

      {/* This section will now stick to the bottom */}
      {(offer.websiteLink || offer.vendorContact) && (
        <div className="loyalty-card-extra">
          {offer.websiteLink && (
            <div className="loyalty-card-link">
              <a
                href={offer.websiteLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Перейти на сайт ${offer.name}`}
              >
                САЙТ КОМПАНИИ
              </a>
            </div>
          )}
          {offer.vendorContact && (
            <div className="loyalty-card-contact">
              <a
                href={
                  offer.vendorContact.includes("@")
                    ? `mailto:${offer.vendorContact}`
                    : `https://wa.me/${offer.vendorContact.replace(/[^\d]/g, "")}`
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                {offer.vendorContact.includes("@")
                  ? offer.vendorContact
                  : formatPhoneNumber(offer.vendorContact)}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

LoyaltyOfferCard.displayName = "LoyaltyOfferCard";

export default LoyaltyOfferCard;
